import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
    Fuel,
    Truck,
    Gauge,
    User,
    MapPin,
    Loader2,
    CheckCircle,
    AlertTriangle,
    Navigation,
    Info,
    WifiOff,
} from 'lucide-react';
import {
    savePendingCarga,
    cacheVehiculos,
    cacheConductores,
    getCachedVehiculos,
    getCachedConductores,
} from '../../lib/dieselOfflineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DieselNuevaCargaPage() {
    const navigate = useNavigate();
    const { nombreSistema } = useDiesel();
    const [vehiculos, setVehiculos] = useState([]);
    const [conductores, setConductores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [gpsStatus, setGpsStatus] = useState('idle'); // idle, loading, success, error
    const [coords, setCoords] = useState(null);

    const [formData, setFormData] = useState({
        vehiculo_id: '',
        litros_cargados: '',
        kilometraje_actual: '',
        tipo_carga: 'completa',
        conductor_id: '',
        nombre_quien_carga: '',
        observaciones: '',
    });

    const [selectedVehiculo, setSelectedVehiculo] = useState(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        loadData();
        getLocation();
        const goOnline = () => setIsOffline(false);
        const goOffline = () => setIsOffline(true);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    const loadData = async () => {
        try {
            const [vRes, cRes] = await Promise.all([
                fetch(`${API_URL}/api/diesel/vehiculos?estado=activo`, { headers }),
                fetch(`${API_URL}/api/diesel/conductores?estado=activo`, { headers }),
            ]);

            let vData = [];
            let cData = [];
            if (vRes.ok) { vData = await vRes.json(); setVehiculos(vData); }
            if (cRes.ok) { cData = await cRes.json(); setConductores(cData); }

            // Cache for offline use
            if (vData.length > 0) cacheVehiculos(vData).catch(() => {});
            if (cData.length > 0) cacheConductores(cData).catch(() => {});
        } catch {
            // Offline - load from IndexedDB cache
            try {
                const [cachedV, cachedC] = await Promise.all([
                    getCachedVehiculos(),
                    getCachedConductores(),
                ]);
                if (cachedV.length > 0) setVehiculos(cachedV);
                if (cachedC.length > 0) setConductores(cachedC);
                if (cachedV.length > 0) toast.info('Datos cargados desde cache offline');
            } catch {
                toast.error('Sin datos disponibles offline');
            }
        } finally {
            setLoading(false);
        }
    };

    const getLocation = () => {
        if (!navigator.geolocation) {
            setGpsStatus('error');
            return;
        }

        setGpsStatus('loading');
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setCoords({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
                setGpsStatus('success');
            },
            (error) => {
                console.error('GPS Error:', error);
                setGpsStatus('error');
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleVehiculoChange = (vehiculoId) => {
        setFormData({ ...formData, vehiculo_id: vehiculoId });
        const vehiculo = vehiculos.find(v => v.id === vehiculoId);
        setSelectedVehiculo(vehiculo);

        // Prellenar conductor asignado si existe
        if (vehiculo?.conductores_asignados?.length > 0) {
            setFormData(prev => ({
                ...prev,
                vehiculo_id: vehiculoId,
                conductor_id: vehiculo.conductores_asignados[0],
            }));
        } else if (vehiculo?.conductor_asignado_id) {
            setFormData(prev => ({
                ...prev,
                vehiculo_id: vehiculoId,
                conductor_id: vehiculo.conductor_asignado_id,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.vehiculo_id || !formData.litros_cargados || !formData.kilometraje_actual) {
            toast.error('Complete los campos obligatorios');
            return;
        }

        setSaving(true);
        const payload = {
            ...formData,
            litros_cargados: parseFloat(formData.litros_cargados),
            kilometraje_actual: parseFloat(formData.kilometraje_actual),
            latitud: coords?.lat,
            longitud: coords?.lng,
        };

        // If offline, save to IndexedDB
        if (!navigator.onLine) {
            try {
                await savePendingCarga(payload);
                toast.success('Carga guardada offline', {
                    description: 'Se sincronizara automaticamente al recuperar conexion',
                });
                navigate('/diesel/cargas');
            } catch {
                toast.error('Error al guardar offline');
            } finally {
                setSaving(false);
            }
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/diesel/cargas`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                const data = await res.json();
                
                if (data.alerta_generada) {
                    toast.warning('Carga registrada con alerta generada', {
                        description: 'Se detecto una anomalia en el rendimiento',
                    });
                } else if (data.es_carga_inicial) {
                    toast.success('Primera carga registrada', {
                        description: 'Esta es la carga inicial del vehiculo',
                    });
                } else {
                    toast.success('Carga registrada exitosamente', {
                        description: data.rendimiento_calculado 
                            ? `Rendimiento: ${data.rendimiento_calculado} km/l`
                            : undefined,
                    });
                }

                navigate('/diesel/cargas');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error al registrar carga');
            }
        } catch {
            // Network error - save offline
            try {
                await savePendingCarga(payload);
                toast.success('Sin conexion - carga guardada offline', {
                    description: 'Se sincronizara al recuperar conexion',
                });
                navigate('/diesel/cargas');
            } catch {
                toast.error('Error al guardar');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <DieselLayout>
            <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
                <div>
                    <h1 className="font-heading text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                        <Fuel className="h-8 w-8" style={{ color: 'var(--diesel-primary)' }} />
                        Nueva Carga
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Registrar carga de combustible
                    </p>
                    {isOffline && (
                        <div className="mt-2 flex items-center gap-2 text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-sm" data-testid="offline-mode-indicator">
                            <WifiOff className="h-4 w-4" />
                            <span className="font-medium">Modo Offline</span>
                            <span className="text-xs opacity-75">- La carga se guardara localmente</span>
                        </div>
                    )}
                </div>

                {/* GPS Status */}
                <Card className={`border-2 ${
                    gpsStatus === 'success' ? 'border-emerald-500 bg-emerald-500/5' :
                    gpsStatus === 'error' ? 'border-amber-500 bg-amber-500/5' :
                    'border-border'
                }`}>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            {gpsStatus === 'loading' && (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                    <span className="text-sm">Obteniendo ubicación GPS...</span>
                                </>
                            )}
                            {gpsStatus === 'success' && (
                                <>
                                    <MapPin className="h-5 w-5 text-emerald-600" />
                                    <div className="flex-1">
                                        <span className="text-sm font-medium text-emerald-600">Ubicación capturada</span>
                                        <p className="text-xs text-muted-foreground">
                                            {coords?.lat.toFixed(6)}, {coords?.lng.toFixed(6)}
                                        </p>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={getLocation}>
                                        <Navigation className="h-4 w-4" />
                                    </Button>
                                </>
                            )}
                            {gpsStatus === 'error' && (
                                <>
                                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                                    <div className="flex-1">
                                        <span className="text-sm font-medium text-amber-600">GPS no disponible</span>
                                        <p className="text-xs text-muted-foreground">La carga se registrará sin ubicación</p>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={getLocation}>
                                        Reintentar
                                    </Button>
                                </>
                            )}
                            {gpsStatus === 'idle' && (
                                <>
                                    <MapPin className="h-5 w-5 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">Esperando GPS...</span>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <form onSubmit={handleSubmit}>
                    <Card className="border-2">
                        <CardHeader>
                            <CardTitle>Datos de la Carga</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Vehículo */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Truck className="h-4 w-4" />
                                    Vehículo *
                                </Label>
                                <select
                                    className="w-full p-3 border-2 rounded-lg bg-background text-lg"
                                    value={formData.vehiculo_id}
                                    onChange={(e) => handleVehiculoChange(e.target.value)}
                                    required
                                >
                                    <option value="">Seleccionar vehículo</option>
                                    {vehiculos.map((v) => (
                                        <option key={v.id} value={v.id}>
                                            {v.patente} - {v.modelo_info?.marca} {v.modelo_info?.nombre}
                                        </option>
                                    ))}
                                </select>
                                {selectedVehiculo?.ultima_carga && (
                                    <div className="p-3 bg-muted rounded-lg text-sm">
                                        <p className="font-medium">Última carga:</p>
                                        <p>KM: <span className="font-mono">{selectedVehiculo.ultima_carga.kilometraje?.toLocaleString()}</span></p>
                                        <p>Litros: <span className="font-mono">{selectedVehiculo.ultima_carga.litros}L</span></p>
                                        <p className="text-xs text-muted-foreground">
                                            {new Date(selectedVehiculo.ultima_carga.fecha).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Kilometraje y Litros */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Gauge className="h-4 w-4" />
                                        Kilometraje Actual *
                                    </Label>
                                    <Input
                                        type="number"
                                        value={formData.kilometraje_actual}
                                        onChange={(e) => setFormData({ ...formData, kilometraje_actual: e.target.value })}
                                        placeholder="123456"
                                        className="text-xl font-mono p-6"
                                        required
                                        min="0"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Fuel className="h-4 w-4" />
                                        Litros Cargados *
                                    </Label>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        value={formData.litros_cargados}
                                        onChange={(e) => setFormData({ ...formData, litros_cargados: e.target.value })}
                                        placeholder="150.5"
                                        className="text-xl font-mono p-6"
                                        required
                                        min="0.1"
                                    />
                                </div>
                            </div>

                            {/* Tipo de carga */}
                            <div className="space-y-2">
                                <Label>Tipo de Carga</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { value: 'completa', label: 'Completa', desc: 'Tanque lleno' },
                                        { value: 'parcial', label: 'Parcial', desc: 'Carga parcial' },
                                        { value: 'emergencia', label: 'Emergencia', desc: 'No afecta cálculos' },
                                    ].map((tipo) => (
                                        <button
                                            key={tipo.value}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, tipo_carga: tipo.value })}
                                            className={`p-3 rounded-lg border-2 text-center transition-colors ${
                                                formData.tipo_carga === tipo.value
                                                    ? 'border-[var(--diesel-primary)] bg-[var(--diesel-primary)]/10'
                                                    : 'border-border hover:border-muted-foreground'
                                            }`}
                                        >
                                            <p className="font-medium">{tipo.label}</p>
                                            <p className="text-xs text-muted-foreground">{tipo.desc}</p>
                                        </button>
                                    ))}
                                </div>
                                {formData.tipo_carga === 'parcial' && (
                                    <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                                        <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                                        <p className="text-sm text-blue-600">
                                            El rendimiento se calculará cuando registre una carga completa
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Conductor */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    Conductor
                                </Label>
                                <select
                                    className="w-full p-3 border-2 rounded-lg bg-background"
                                    value={formData.conductor_id}
                                    onChange={(e) => setFormData({ ...formData, conductor_id: e.target.value })}
                                >
                                    <option value="">Seleccionar conductor (opcional)</option>
                                    {conductores.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.nombre} ({c.rut})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Quien carga */}
                            <div className="space-y-2">
                                <Label>Nombre de quien carga</Label>
                                <Input
                                    value={formData.nombre_quien_carga}
                                    onChange={(e) => setFormData({ ...formData, nombre_quien_carga: e.target.value })}
                                    placeholder="Nombre del operador"
                                />
                            </div>

                            {/* Observaciones */}
                            <div className="space-y-2">
                                <Label>Observaciones</Label>
                                <Textarea
                                    value={formData.observaciones}
                                    onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                                    placeholder="Notas adicionales..."
                                    rows={3}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Botón Submit */}
                    <div className="flex gap-3 mt-6">
                        <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            onClick={() => navigate('/diesel/cargas')}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className="flex-1 bg-[var(--diesel-primary)] text-[var(--diesel-secondary)] h-14 text-lg"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                    Registrando...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    Registrar Carga
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </DieselLayout>
    );
}
